import { Hash, Tag, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';

interface TagData {
  tag: string;
  count: number;
  type?: 'topic' | 'audience' | 'complexity' | 'goal';
}

interface TagsSidebarProps {
  tags: TagData[];
  title?: string;
  maxTags?: number;
  showViewAll?: boolean;
}

export function TagsSidebar({ 
  tags, 
  title = "Popular Tags", 
  maxTags = 15,
  showViewAll = true 
}: TagsSidebarProps) {
  // Sort tags by count and limit
  const sortedTags = tags
    .sort((a, b) => b.count - a.count)
    .slice(0, maxTags);

  const getTagIcon = (type?: string) => {
    switch (type) {
      case 'topic': return <Hash className="h-3 w-3" />;
      case 'audience': return <Tag className="h-3 w-3" />;
      case 'complexity': return <TrendingUp className="h-3 w-3" />;
      case 'goal': return <Tag className="h-3 w-3" />;
      default: return <Tag className="h-3 w-3" />;
    }
  };

  const getTagLabel = (tag: string) => {
    return tag.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (sortedTags.length === 0) {
    return null;
  }

  return (
    <div className="hidden lg:block sticky top-[5rem]">
      <div className="sidebar-card">
      <h2 className="sidebar-header">
        <div className="sidebar-header-accent"></div>
        {title}
      </h2>
      <div className="space-y-2">
        {sortedTags.map(({ tag, count, type }) => (
          <Link
            key={tag}
            to={`/tag/${tag}`}
            className="block group link-underline"
          >
            <article className="relative pl-5 pb-1 hover:translate-x-1 transition-all duration-200">
              <div className="absolute left-0 top-1 w-1.5 h-1.5 bg-secondary rounded-full group-hover:bg-primary transition-colors"></div>
              <div className="absolute left-0.5 top-2.5 w-0.5 h-full bg-gradient-to-b from-secondary/30 to-transparent"></div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-medium text-foreground truncate">
                    {getTagLabel(tag)}
                  </span>
                </div>
                <Badge 
                  variant="secondary" 
                  className="text-[10px] bg-secondary/20 text-secondary flex-shrink-0 ml-2 group-hover:bg-primary/20 group-hover:text-primary transition-colors px-1.5 py-0"
                >
                  {count}
                </Badge>
              </div>
              
              <div className="flex items-center gap-1 text-[9px] text-muted-foreground mt-0.5">
                {getTagIcon(type)}
                <span>
                  {type === 'topic' && 'Topic'}
                  {type === 'audience' && 'Audience'}
                  {type === 'complexity' && 'Complexity'}
                  {type === 'goal' && 'Goal'}
                  {!type && 'Tag'}
                </span>
              </div>
            </article>
          </Link>
        ))}
        
        {showViewAll && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(74, 144, 226, 0.2)' }}>
            <Link to="/tags" className="sidebar-view-all-btn text-xs">
              <span>All Tags</span>
              <ArrowRight className="ml-1.5 h-3 w-3" />
            </Link>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}